// Vimeo utility functions for video integration
// Replace with your actual Vimeo access token
const VIMEO_ACCESS_TOKEN = 'YOUR_VIMEO_ACCESS_TOKEN_HERE'

export interface VimeoVideo {
  id: string
  title: string
  description: string
  thumbnail: string
  duration: string
  publishedAt: string
  viewCount: number
  tags: string[]
}

// Extract video ID from Vimeo URL (enhanced)
export function extractVimeoId(url: string): string | null {
  if (!url) return null
  
  // If it's already just an ID (numeric)
  if (/^\d+$/.test(url.trim())) {
    return url.trim()
  }
  
  // Extract from various Vimeo URL formats
  const patterns = [
    /vimeo\.com\/(\d+)/,
    /vimeo\.com\/channels\/[^/]+\/(\d+)/,
    /vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
    /vimeo\.com\/album\/\d+\/video\/(\d+)/,
    /vimeo\.com\/video\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) {
      return match[1]
    }
  }
  
  return null
}

// Extract h parameter from Vimeo URL for unlisted videos
export function extractVimeoH(url: string): string | null {
  if (!url) return null
  
  const hMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/)
  return hMatch ? hMatch[1] : null
}

// Extract both video ID and h parameter from Vimeo URL
export function extractVimeoParams(url: string): { videoId: string | null, h: string | null } {
  return {
    videoId: extractVimeoId(url),
    h: extractVimeoH(url)
  }
}

// Generate thumbnail URL from Vimeo video ID using oEmbed API
export async function getVimeoThumbnail(videoId: string, h?: string | null): Promise<string> {
  try {
    // Use player.vimeo.com format when h parameter exists, otherwise use vimeo.com
    const vimeoUrl = h 
      ? `https://player.vimeo.com/video/${videoId}?h=${h}`
      : `https://vimeo.com/${videoId}`
    const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}`)
    const data = await response.json()
    return data.thumbnail_url || '/api/placeholder/640/360'
  } catch (error) {
    console.error('Error fetching Vimeo thumbnail:', error)
    return '/api/placeholder/640/360'
  }
}

// Convert seconds to readable duration format
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Fetch video details from Vimeo API
export async function fetchVimeoVideoDetails(videoId: string): Promise<VimeoVideo | null> {
  try {
    const response = await fetch(
      `https://api.vimeo.com/videos/${videoId}`,
      {
        headers: {
          'Authorization': `Bearer ${VIMEO_ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      throw new Error('Failed to fetch video details')
    }
    
    const video = await response.json()
    
    return {
      id: video.uri.split('/').pop(),
      title: video.name,
      description: video.description || '',
      thumbnail: video.pictures?.sizes?.[4]?.link || '/api/placeholder/640/360',
      duration: formatDuration(video.duration),
      publishedAt: video.created_time,
      viewCount: video.stats?.plays || 0,
      tags: video.tags?.map((tag: any) => tag.name) || []
    }
    
  } catch (error) {
    console.error('Error fetching Vimeo video details:', error)
    return null
  }
}

// Fetch videos from a Vimeo user/channel
export async function fetchVimeoUserVideos(userId: string, maxResults: number = 50): Promise<VimeoVideo[]> {
  try {
    const response = await fetch(
      `https://api.vimeo.com/users/${userId}/videos?per_page=${maxResults}&sort=date`,
      {
        headers: {
          'Authorization': `Bearer ${VIMEO_ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      throw new Error('Failed to fetch user videos')
    }
    
    const data = await response.json()
    
    return data.data.map((video: any) => ({
      id: video.uri.split('/').pop(),
      title: video.name,
      description: video.description || '',
      thumbnail: video.pictures?.sizes?.[4]?.link || '/api/placeholder/640/360',
      duration: formatDuration(video.duration),
      publishedAt: video.created_time,
      viewCount: video.stats?.plays || 0,
      tags: video.tags?.map((tag: any) => tag.name) || []
    }))
    
  } catch (error) {
    console.error('Error fetching user videos:', error)
    return []
  }
}

// Search for Vimeo videos
export async function searchVimeoVideos(query: string, maxResults: number = 50): Promise<VimeoVideo[]> {
  try {
    const response = await fetch(
      `https://api.vimeo.com/videos?query=${encodeURIComponent(query)}&per_page=${maxResults}`,
      {
        headers: {
          'Authorization': `Bearer ${VIMEO_ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      throw new Error('Failed to search videos')
    }
    
    const data = await response.json()
    
    return data.data.map((video: any) => ({
      id: video.uri.split('/').pop(),
      title: video.name,
      description: video.description || '',
      thumbnail: video.pictures?.sizes?.[4]?.link || '/api/placeholder/640/360',
      duration: formatDuration(video.duration),
      publishedAt: video.created_time,
      viewCount: video.stats?.plays || 0,
      tags: video.tags?.map((tag: any) => tag.name) || []
    }))
    
  } catch (error) {
    console.error('Error searching videos:', error)
    return []
  }
}

// Get embed URL for Vimeo video
export function getVimeoEmbedUrl(videoId: string, options: {
  autoplay?: boolean
  loop?: boolean
  muted?: boolean
  controls?: boolean
  badge?: boolean
  h?: string
} = {}): string {
  const params = new URLSearchParams()
  
  if (options.autoplay) params.set('autoplay', '1')
  if (options.loop) params.set('loop', '1')
  if (options.muted) params.set('muted', '1')
  if (options.controls === false) params.set('controls', '0')
  if (options.badge === false) params.set('badge', '0')
  if (options.h) params.set('h', options.h)
  
  const queryString = params.toString()
  return `https://player.vimeo.com/video/${videoId}${queryString ? `?${queryString}` : ''}`
}

// Sample video data for development (replace with real data)
export const sampleVimeoVideos: VimeoVideo[] = [
  {
    id: '123456789',
    title: 'アントレプレナーシップ（起業）入門',
    description: 'アントレプレナーシップの基本を学ぶ入門編の動画です。',
    thumbnail: '/api/placeholder/640/360',
    duration: '10:30',
    publishedAt: '2024-03-15T00:00:00Z',
    viewCount: 1250,
    tags: ['起業', 'アントレ', '入門']
  },
  {
    id: '234567890',
    title: 'インタビューとニーズ分析',
    description: '顧客インタビューの方法とニーズ分析の技術を学びます。',
    thumbnail: '/api/placeholder/640/360',
    duration: '15:45',
    publishedAt: '2024-03-10T00:00:00Z',
    viewCount: 2100,
    tags: ['インタビュー', '分析', 'ニーズ']
  },
  {
    id: '345678901',
    title: 'アンケートの基礎',
    description: '効果的なアンケート作成の基礎を学びます。',
    thumbnail: '/api/placeholder/640/360',
    duration: '8:20',
    publishedAt: '2024-03-08T00:00:00Z',
    viewCount: 980,
    tags: ['アンケート', '基礎', 'リサーチ']
  }
]

// Vimeo player configuration options
export const defaultVimeoOptions = {
  badge: false,
  autopause: false,
  autoplay: false,
  controls: true,
  responsive: true
}

// Enhanced video validation using oEmbed API (no auth required)
export async function validateVimeoVideo(videoId: string, h?: string | null): Promise<{
  isValid: boolean
  metadata?: any
  error?: string
}> {
  try {
    // Use player.vimeo.com format when h parameter exists, otherwise use vimeo.com
    const vimeoUrl = h 
      ? `https://player.vimeo.com/video/${videoId}?h=${h}`
      : `https://vimeo.com/${videoId}`
    const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}`)
    
    if (!response.ok) {
      return {
        isValid: false,
        error: `Video not found or not accessible (${response.status})`
      }
    }
    
    const metadata = await response.json()
    return {
      isValid: true,
      metadata
    }
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

// Auto-fetch video metadata for video creation
export async function fetchVimeoMetadata(videoId: string, h?: string | null): Promise<{
  title: string
  description: string
  duration: string
  thumbnail: string
  authorName: string
  width: number
  height: number
}> {
  try {
    // Use player.vimeo.com format when h parameter exists, otherwise use vimeo.com
    const vimeoUrl = h 
      ? `https://player.vimeo.com/video/${videoId}?h=${h}`
      : `https://vimeo.com/${videoId}`
    const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}&width=1280&height=720`)
    
    if (!response.ok) {
      throw new Error(`Failed to fetch video metadata: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.json()
    
    return {
      title: data.title || `Vimeo Video ${videoId}`,
      description: data.description || '',
      duration: formatDuration(data.duration || 0),
      thumbnail: data.thumbnail_url || `https://vumbnail.com/${videoId}.jpg`,
      authorName: data.author_name || '',
      width: data.width || 1280,
      height: data.height || 720
    }
  } catch (error) {
    console.error('Error fetching Vimeo metadata:', error)
    throw new Error(`Failed to fetch video metadata: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Generate alternative thumbnail URL using vumbnail service
export function getVimeoThumbnailUrl(videoId: string, size: 'small' | 'medium' | 'large' = 'medium'): string {
  if (!videoId) return ''
  
  const sizeMap = {
    small: '320',
    medium: '640',
    large: '960'
  }
  
  return `https://vumbnail.com/${videoId}_${sizeMap[size]}.jpg`
}

// Auto-import video data from Vimeo
export async function autoImportVimeoVideo(videoId: string, category: string = 'beginner', h?: string | null) {
  const validation = await validateVimeoVideo(videoId, h)
  
  if (!validation.isValid) {
    throw new Error(validation.error || 'Video not accessible')
  }
  
  const metadata = await fetchVimeoMetadata(videoId, h)
  
  // Generate suggested tags based on title and description
  const suggestedTags = generateSuggestedTags(metadata.title, metadata.description)
  
  return {
    title: metadata.title,
    description: metadata.description,
    vimeo_id: videoId,
    vimeo_h: h || '', // Use the provided h parameter
    duration: metadata.duration,
    thumbnail_url: metadata.thumbnail,
    category,
    tags: suggestedTags,
    view_count: 0,
    published_at: new Date().toISOString()
  }
}

// Generate suggested tags from title and description
function generateSuggestedTags(title: string, description: string): string[] {
  const text = `${title} ${description}`.toLowerCase()
  const keywords = [
    'アントレプレナーシップ', '起業', 'ビジネス', 'マーケティング',
    'インタビュー', 'アンケート', 'リサーチ', '調査',
    'プレゼンテーション', 'コンテスト', 'ピッチ',
    'チームビルディング', 'リーダーシップ', 'マネジメント',
    '財務', '資金調達', 'イノベーション', '創造性',
    '基礎', '入門', '実践', '応用'
  ]
  
  return keywords.filter(keyword => 
    text.includes(keyword.toLowerCase()) || 
    text.includes(keyword)
  ).slice(0, 5) // Limit to 5 tags
}